import React from "react";
import { FILTER_METHODS } from "./filterMethodsConfig";

export default function ReelsMetricsSection({
  reelsMetrics,
  state,
  placeholders: globalPlaceholders,
  showPromptPreview,
  r,
  f,
  t,
  updateState,
  updateReelsMetric,
  setShowPromptPreview,
}) {
  const selectedMethods = reelsMetrics.filterMethods || [];
  const [showFilterPromptPreview, setShowFilterPromptPreview] = React.useState(false);
  const [showDescPromptPreview, setShowDescPromptPreview] = React.useState(true);

  // Мемоизируем r через ref
  const rRef = React.useRef(r);
  rRef.current = r;

  const handleMethodToggle = (methodKey) => {
    const current = [...selectedMethods];
    const index = current.indexOf(methodKey);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(methodKey);
    }

    updateReelsMetric("filterMethods", current);

    if (current.length > 0 && !state.aiMiddlePart) {
      updateState("aiMiddlePart", r.aiPromptPlaceholder || "Describe what you want to find...");
    }
  };

  const getAvailablePlaceholders = () => {
    if (selectedMethods.length === 0) return globalPlaceholders;

    const placeholders = new Map();
    selectedMethods.forEach((methodKey) => {
      const method = FILTER_METHODS[methodKey];
      if (method) {
        method.placeholders.forEach((p) => {
          placeholders.set(p.key, p);
        });
      }
    });
    return Array.from(placeholders.values());
  };

  const availablePlaceholders = getAvailablePlaceholders();

  // Функция построения промпта ДЛЯ ФИЛЬТРАЦИИ (без "IMPORTANT: Result passes...")
  const buildFilterPrompt = React.useCallback(() => {
    if (!state.useAI || !reelsMetrics.needFilter) return "";

    const translations = rRef.current;
    let prompt = translations.promptPart1FilterOnly || "Analyze this Instagram Reel.\n\nRespond in this exact format:\nLine 1: 'yes' or 'no'\n\n";

    if (state.aiMiddlePart?.trim()) {
      prompt += state.aiMiddlePart.trim();
    }

    // УБРАЛИ pass condition из промпта
    return prompt.trim();
  }, [state.useAI, state.aiMiddlePart, reelsMetrics.needFilter]);

  // Функция построения промпта ДЛЯ ГЕНЕРАЦИИ ОПИСАНИЯ
  const buildDescriptionPrompt = React.useCallback(() => {
    if (!state.useAI || !reelsMetrics.needDescription) return "";

    const translations = rRef.current;
    let prompt = translations.promptPart1DescOnly || "Watch this Instagram Reel video and provide a detailed description of what happens.\n\nDescribe:\n- Visual content (scenes, objects, people)\n- Actions and activities\n- Text visible in the video\n- Audio/music (if identifiable)\n- Overall mood and style\n\nProvide a comprehensive description in 3-5 sentences.\n\n";

    return prompt.trim();
  }, [state.useAI, reelsMetrics.needDescription]);

  // Предотвращаем бесконечный цикл
  const prevPromptRef = React.useRef("");

  React.useEffect(() => {
    if (!state.useAI) {
      prevPromptRef.current = "";
      return;
    }

    let fullPrompt = "";

    if (reelsMetrics.needFilter) {
      fullPrompt = buildFilterPrompt();
    }

    if (reelsMetrics.needFilter && reelsMetrics.needDescription) {
      fullPrompt += "\n\n---\n\n" + buildDescriptionPrompt();
    } else if (!reelsMetrics.needFilter && reelsMetrics.needDescription) {
      fullPrompt = buildDescriptionPrompt();
    }

    if (fullPrompt && fullPrompt !== prevPromptRef.current) {
      prevPromptRef.current = fullPrompt;
      updateState("aiPrompt", fullPrompt);
    }
  }, [
    state.useAI,
    reelsMetrics.needFilter,
    reelsMetrics.needDescription,
    buildFilterPrompt,
    buildDescriptionPrompt,
    updateState,
  ]);

  // ==================== РАСЧЕТ ТОКЕНОВ ====================
  const TOKENS_PER_VIDEO_FILTER = 20000; // 20k токенов за видео-фильтрацию
  const TOKENS_PER_TEXT_FILTER = 600;    // 600 токенов за текстовую фильтрацию
  const TOKENS_PER_MINUTE_DESC = 20000;  // 20k токенов за минуту видео для описания
  const AVG_REEL_DURATION_SECONDS = 45;
  const estimatedDescTokensPerReel = Math.round((AVG_REEL_DURATION_SECONDS / 60) * TOKENS_PER_MINUTE_DESC);
  const topReelsCount = parseInt(reelsMetrics.topReelsCount) || 30;

  // Расчет токенов для ФИЛЬТРАЦИИ
  const calculateFilterTokens = () => {
    if (!reelsMetrics.needFilter || selectedMethods.length === 0) return { tokens: 0, breakdown: "" };

    const hasVideoMethod = selectedMethods.some((key) => FILTER_METHODS[key]?.isVideoAnalysis);
    const hasTextMethod = selectedMethods.some((key) => !FILTER_METHODS[key]?.isVideoAnalysis);

    let totalTokens = 0;
    let breakdown = [];

    if (hasVideoMethod) {
      totalTokens += TOKENS_PER_VIDEO_FILTER;
      breakdown.push(`Video analysis: ~${TOKENS_PER_VIDEO_FILTER.toLocaleString()} tokens/reel`);
    }
    if (hasTextMethod) {
      totalTokens += TOKENS_PER_TEXT_FILTER;
      breakdown.push(`Text/profile analysis: ~${TOKENS_PER_TEXT_FILTER.toLocaleString()} tokens/reel`);
    }

    return {
      tokens: totalTokens,
      breakdown: breakdown.join(" + "),
      totalFormatted: `${totalTokens.toLocaleString()} tokens per reel`,
    };
  };

  // Расчет токенов для DESCRIPTION
  const calculateDescriptionTokens = () => {
    if (!reelsMetrics.needDescription) return { tokens: 0, totalTokens: 0 };

    const perReel = estimatedDescTokensPerReel;
    const total = perReel * topReelsCount;

    return {
      perReel,
      total,
      perReelFormatted: `~${perReel.toLocaleString()} tokens/reel`,
      totalFormatted: `~${total.toLocaleString()} tokens for ${topReelsCount} reels`,
    };
  };

  const filterTokens = calculateFilterTokens();
  const descTokens = calculateDescriptionTokens();

  return (
    <>
      {/* Step 1: Metrics Filter */}
      <div className="section-card">
        <h3>
          {r.step1MetricsFilter}
          <span className="optional-badge">{f.optional}</span>
          <label className="reels-enable-toggle">
            <input
              type="checkbox"
              checked={reelsMetrics.enabled}
              onChange={(e) => updateReelsMetric("enabled", e.target.checked)}
            />
            <span>{f.enable}</span>
          </label>
        </h3>
        <p className="section-sub">{r.metricsFilterDesc}</p>

        {reelsMetrics.enabled && (
          <div className="reels-metrics-grid">
            <div className="reels-metric-row">
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.minViews}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={reelsMetrics.minViews}
                  onChange={(e) => updateReelsMetric("minViews", e.target.value)}
                />
              </div>
              <span className="reels-metric-sep"></span>
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.maxViews}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="No limit"
                  value={reelsMetrics.maxViews}
                  onChange={(e) => updateReelsMetric("maxViews", e.target.value)}
                />
              </div>
            </div>

            <div className="reels-metric-row">
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.minLikes}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={reelsMetrics.minLikes}
                  onChange={(e) => updateReelsMetric("minLikes", e.target.value)}
                />
              </div>
              <span className="reels-metric-sep"></span>
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.maxLikes}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="No limit"
                  value={reelsMetrics.maxLikes}
                  onChange={(e) => updateReelsMetric("maxLikes", e.target.value)}
                />
              </div>
            </div>

            <div className="reels-metric-row">
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.minComments}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={reelsMetrics.minComments}
                  onChange={(e) => updateReelsMetric("minComments", e.target.value)}
                />
              </div>
              <span className="reels-metric-sep"></span>
              <div className="reels-metric-field">
                <label className="reels-metric-label">{r.maxComments}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="No limit"
                  value={reelsMetrics.maxComments}
                  onChange={(e) => updateReelsMetric("maxComments", e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: AI Filtering */}
      <div className="section-card">
        <h3>
          {r.step2AiFiltering || "Step 2: AI Filtering"}
          <span className="optional-badge">{f.optional}</span>
          <label className="reels-enable-toggle">
            <input
              type="checkbox"
              checked={reelsMetrics.needFilter}
              onChange={(e) => updateReelsMetric("needFilter", e.target.checked)}
            />
            <span>{f.enable}</span>
          </label>
        </h3>
        <p className="section-sub">{r.aiFilteringDesc || "AI will classify each reel based on your criteria"}</p>

        {reelsMetrics.needFilter && (
          <div className="reels-ai-content">
            {/* Filter Methods Selection */}
            <div className="reels-field-block">
              <label className="advanced-field-label">
                {r.filterMethods || "Filter Methods"}
                <span className="required-badge">*</span>
              </label>
              <p className="reels-field-hint">
                {r.filterMethodsDesc || "Select one or more methods for AI to analyze the reels. You can combine multiple methods."}
              </p>

              <div className="filter-methods-grid">
                {Object.values(FILTER_METHODS).map((method) => (
                  <label
                    key={method.key}
                    className={`filter-method-card ${selectedMethods.includes(method.key) ? "selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMethods.includes(method.key)}
                      onChange={() => handleMethodToggle(method.key)}
                    />
                    <div className="filter-method-content">
                      <div className="filter-method-title">
                        {method.title}
                        {method.isVideoAnalysis && (
                          <span className="filter-method-badge">🎬 Video</span>
                        )}
                      </div>
                      <div className="filter-method-desc">{method.description}</div>
                      <div className="filter-method-vars">
                        Variables: {method.placeholders.map((p) => `[[${p.key}]]`).join(", ")}
                      </div>
                      {method.isVideoAnalysis && (
                        <div className="filter-method-cost-warning">
                          ⚠️ ~{TOKENS_PER_VIDEO_FILTER.toLocaleString()} tokens per reel
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Стоимость фильтрации */}
              {selectedMethods.length > 0 && (
                <div className="reels-token-info" style={{ marginTop: 12, background: "linear-gradient(135deg, #e8f4fd 0%, #dceefb 100%)", border: "1px solid #93c5fd" }}>
                  <div className="reels-token-info-icon">💰</div>
                  <div className="reels-token-info-text">
                    <div className="reels-token-info-title" style={{ color: "#1e40af" }}>
                      {r.filterCostEstimate || "Filter Cost Estimate"}
                    </div>
                    <div className="reels-token-info-desc" style={{ color: "#1e3a8a" }}>
                      {filterTokens.breakdown && `${filterTokens.breakdown}\n`}
                      {filterTokens.totalFormatted}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Filter Prompt */}
            {selectedMethods.length > 0 && (
              <>
                <div className="reels-field-block">
                  <label className="advanced-field-label">{r.yourPrompt || "Your Filter Prompt"}</label>
                  <textarea
                    className="intent-textarea"
                    placeholder={r.aiPromptPlaceholder || "What should the AI check?"}
                    value={state.aiMiddlePart}
                    onChange={(e) => updateState("aiMiddlePart", e.target.value)}
                    style={{ minHeight: 200 }}
                  />
                </div>

                {/* Available Placeholders */}
                {availablePlaceholders.length > 0 && (
                  <div className="reels-field-block">
                    <label className="advanced-field-label">
                      {r.availablePlaceholders || "Available Placeholders"}
                    </label>
                    <div className="placeholder-buttons">
                      {availablePlaceholders.map(({ key, label }) => (
                        <button
                          key={key}
                          className="placeholder-chip"
                          onClick={() =>
                            updateState("aiMiddlePart", state.aiMiddlePart + ` [[${key}]]`)
                          }
                          title={label}
                        >
                          [[{key}]] - {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Match Condition */}
                <div className="reels-field-block">
                  <label className="advanced-field-label">
                    {r.matchCondition || "Pass Condition"}
                    <span className="required-badge">*</span>
                  </label>
                  <p className="reels-field-hint">{r.matchConditionDesc}</p>
                  <input
                    className="form-input"
                    type="text"
                    placeholder={r.wordToMatchPlaceholder || "e.g. yes"}
                    value={state.matchWord}
                    onChange={(e) => updateState("matchWord", e.target.value)}
                  />
                </div>

                {/* Prompt Preview для фильтрации */}
                <div className="reels-prompt-preview">
                  <button
                    className="reels-prompt-preview-toggle"
                    onClick={() => setShowFilterPromptPreview(!showFilterPromptPreview)}
                  >
                    {r.filterPromptPreview || "Filter Prompt Preview"}
                    <span
                      className={`reels-prompt-preview-arrow ${showFilterPromptPreview ? "open" : ""}`}
                    >
                      &#9660;
                    </span>
                  </button>

                  {showFilterPromptPreview && (
                    <div className="reels-prompt-preview-box">
                      <pre className="reels-prompt-preview-text">
                        {buildFilterPrompt() || "No prompt generated yet"}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Если методы не выбраны */}
            {selectedMethods.length === 0 && (
              <div className="reels-notice reels-notice-warning">
                {r.selectAtLeastOneMethod || "⚠️ Select at least one filter method above"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 3: Sorting & Selection */}
      <div className="section-card">
        <h3>{r.step3Sorting || "Step 3: Sorting & Selection"}</h3>
        <p className="section-sub">{r.sortingDesc}</p>

        <div className="reels-sorting-grid">
          <div className="reels-field-block">
            <label className="advanced-field-label">{r.engagementFormula}</label>
            <div className="match-scope-buttons">
              <button
                className={`match-scope-btn${reelsMetrics.engagementFormula === "standard" ? " selected" : ""}`}
                onClick={() => updateReelsMetric("engagementFormula", "standard")}
              >
                {r.standardFormula}
              </button>
              <button
                className={`match-scope-btn${reelsMetrics.engagementFormula === "simple" ? " selected" : ""}`}
                onClick={() => updateReelsMetric("engagementFormula", "simple")}
              >
                {r.simpleFormula}
              </button>
            </div>
            <div className="reels-formula-hint">
              {reelsMetrics.engagementFormula === "standard"
                ? r.standardFormulaDesc
                : r.simpleFormulaDesc}
            </div>
          </div>

          <div className="reels-field-block">
            <label className="advanced-field-label">{r.sortBy}</label>
            <div className="match-scope-buttons">
              <button
                className={`match-scope-btn${reelsMetrics.sortBy === "engagement" ? " selected" : ""}`}
                onClick={() => updateReelsMetric("sortBy", "engagement")}
              >
                {r.byEngagement}
              </button>
              <button
                className={`match-scope-btn${reelsMetrics.sortBy === "likes" ? " selected" : ""}`}
                onClick={() => updateReelsMetric("sortBy", "likes")}
              >
                {r.byLikes}
              </button>
              <button
                className={`match-scope-btn${reelsMetrics.sortBy === "comments" ? " selected" : ""}`}
                onClick={() => updateReelsMetric("sortBy", "comments")}
              >
                {r.byComments}
              </button>
            </div>
          </div>

          <div className="reels-field-block">
            <label className="advanced-field-label">{r.topReelsCount}</label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="100"
              placeholder="30"
              value={reelsMetrics.topReelsCount}
              onChange={(e) => updateReelsMetric("topReelsCount", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Step 4: Generate Description */}
      <div className="section-card">
        <h3>
          {r.step4GenerateDescription || "Step 4: Generate Description"}
          <span className="optional-badge">{f.optional}</span>
          <label className="reels-enable-toggle">
            <input
              type="checkbox"
              checked={reelsMetrics.needDescription}
              onChange={(e) => updateReelsMetric("needDescription", e.target.checked)}
            />
            <span>{f.enable}</span>
          </label>
        </h3>
        <p className="section-sub">
          {r.generateDescriptionDesc || "AI will watch each reel and generate a detailed description of its content"}
        </p>

        {reelsMetrics.needDescription && (
          <div className="reels-description-content">
            {/* БЛОК 1: Что делает + стоимость */}
            <div className="reels-token-info">
              <div className="reels-token-info-icon">🎬</div>
              <div className="reels-token-info-text">
                <div className="reels-token-info-title">
                  {r.videoAnalysisCost || "Video Analysis Cost"}
                </div>
                <div className="reels-token-info-desc">
                  {r.descriptionHowItWorks || "The AI will analyze the actual video content (visual frames, scenes, objects, actions, text in video) and generate a detailed text description of what happens in the reel."}
                </div>
                <div className="reels-token-info-cost">
                  {descTokens.perReelFormatted}
                  <br />
                  {descTokens.totalFormatted}
                </div>
              </div>
            </div>

            {/* БЛОК 2: Применяется к топ N рилсам */}
            <div className="reels-notice reels-notice-info">
              {r.descriptionAppliedToTop
                ? r.descriptionAppliedToTop.replace("{count}", topReelsCount)
                : `📌 Descriptions will be generated only for the top ${topReelsCount} reels (based on sorting settings in Step 3).`}
            </div>

            {/* Prompt Preview (открыт по умолчанию) */}
            <div className="reels-prompt-preview">
              <button
                className="reels-prompt-preview-toggle"
                onClick={() => setShowDescPromptPreview(!showDescPromptPreview)}
              >
                {r.descPromptPreview || "Description Prompt Preview"}
                <span
                  className={`reels-prompt-preview-arrow ${showDescPromptPreview ? "open" : ""}`}
                >
                  &#9660;
                </span>
              </button>

              {showDescPromptPreview && (
                <div className="reels-prompt-preview-box">
                  <pre className="reels-prompt-preview-text">
                    {buildDescriptionPrompt() || "No prompt generated yet"}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Step 5: AI Summary */}
      <div className="section-card">
        <div className="reels-summary-header">
          <h3>
            {r.step5Summary || "Step 5: AI Summary"}
            <span className="optional-badge">{f.optional}</span>
          </h3>
          <label className="reels-enable-toggle">
            <input
              type="checkbox"
              checked={reelsMetrics.generateSummary}
              onChange={(e) => updateReelsMetric("generateSummary", e.target.checked)}
              disabled={!reelsMetrics.needDescription}
            />
            <span>{f.enable}</span>
          </label>
        </div>
        <p className="section-sub">{r.summaryDesc}</p>

        {!reelsMetrics.needDescription && (
          <div className="reels-notice reels-notice-warning">
            {r.needDescriptionWarning || "⚠️ Enable description generation in Step 4 first"}
          </div>
        )}

        {reelsMetrics.generateSummary && reelsMetrics.needDescription && (
          <div className="reels-summary-content">
            <div className="reels-notice reels-notice-info">
              {r.summaryDataFlow || "Summary receives descriptions + engagement metrics for each top reel"}
            </div>

            <div className="reels-field-block">
              <label className="advanced-field-label">
                {r.summaryPrompt}
                <span className="required-badge">*</span>
              </label>
              <textarea
                className="intent-textarea"
                placeholder={r.summaryPromptPlaceholder}
                value={reelsMetrics.summaryPrompt}
                onChange={(e) => updateReelsMetric("summaryPrompt", e.target.value)}
                style={{ minHeight: 260 }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}